import React, { useState, useEffect } from 'react';
import api from '../../api';
import { useLanguage } from '../../i18n/LanguageContext';
import './Branches.css';

const Branches = () => {
    const { t } = useLanguage();
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: '', code: '', region: '', district: '', parent_id: '', status: 'active' });
    const [showImport, setShowImport] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [search, setSearch] = useState('');

    useEffect(() => {
        loadBranches();
    }, []);

    const loadBranches = async () => {
        setLoading(true);
        try {
            const res = await api.getBranches({ per_page: 200 });
            setBranches(res.data || []);
        } catch (err) {
            console.error('Failed to load branches', err);
            setBranches([]);
        } finally {
            setLoading(false);
        }
    };

    const openForm = (b = null) => {
        if (b) {
            setEditing(b);
            setForm({
                name: b.name || '',
                code: b.code || '',
                region: b.region || '',
                district: b.district || '',
                parent_id: b.parent_id || '',
                status: b.status || 'active'
            });
        } else {
            setEditing(null);
            setForm({ name: '', code: '', region: '', district: '', parent_id: '', status: 'active' });
        }
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditing(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editing) {
                await api.updateBranch(editing.id, form);
            } else {
                await api.createBranch(form);
            }
            closeForm();
            loadBranches();
        } catch (err) {
            console.error('Save branch failed', err);
            alert(err.message || t('Failed to save branch'));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('Je, una uhakika unataka kufuta tawi hili?'))) return;
        try {
            await api.deleteBranch(id);
            loadBranches();
        } catch (err) {
            console.error('Delete branch failed', err);
            alert(t('Failed to delete branch'));
        }
    };

    const normalizedSearch = search.trim().toLowerCase();
    const filteredBranches = branches.filter((branch) => !normalizedSearch ||
        [branch.name, branch.code, branch.region, branch.district]
            .some(value => String(value || '').toLowerCase().includes(normalizedSearch))
    );
    const activeBranches = branches.filter(branch => branch.status === 'active').length;
    const regions = new Set(branches.map(branch => branch.region).filter(Boolean)).size;

    return (
        <div className="branches-page">
            <div className="page-header branches-hero">
                <div className="header-left">
                    <span className="branches-kicker">TAREWU • BRANCH NETWORK</span>
                    <h1 className="page-title">{t('Matawi')}</h1>
                    <p className="page-subtitle">{t('Dhibiti matawi ya chama (branches)')}</p>
                </div>
                <div className="header-actions">
                    <button className="btn btn-secondary" onClick={() => setShowImport(true)}>{t('Import CSV')}</button>
                    <button className="btn btn-primary" onClick={() => openForm()}><span>+</span>{t('Ongeza Tawi')}</button>
                </div>
            </div>

            <div className="branch-summary">
                <div><small>{t('Jumla ya Matawi')}</small><strong>{branches.length}</strong></div>
                <div><small>{t('Active')}</small><strong>{activeBranches}</strong></div>
                <div><small>{t('Mikoa / Maeneo')}</small><strong>{regions}</strong></div>
            </div>

            <div className="branches-toolbar">
                <div className="branch-search">
                    <span aria-hidden="true">⌕</span>
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('Tafuta tawi, msimbo, mkoa au wilaya...')} />
                    {search && <button onClick={() => setSearch('')} aria-label={t('Clear search')}>×</button>}
                </div>
                <span>{filteredBranches.length} {t('matawi')}</span>
            </div>

            <div className="branches-list">
                {loading ? (
                    <div className="loading-spinner" />
                ) : filteredBranches.length > 0 ? (
                    filteredBranches.map(b => (
                        <div key={b.id} className="branch-card">
                            <div className="branch-mark">{(b.code || b.name || 'T').charAt(0).toUpperCase()}</div>
                            <div className="branch-main">
                                <div className="branch-title-row"><div className="branch-title">{b.name}</div><span className={`branch-status ${b.status || 'active'}`}>{b.status || 'active'}</span></div>
                                <div className="branch-code">{b.code || t('No code')}</div>
                                <div className="branch-meta"><span>◉ {b.region || '—'}</span><span>□ {b.district || t('No district')}</span></div>
                            </div>
                            <div className="branch-actions">
                                <button className="btn btn-sm" onClick={() => openForm(b)}>{t('Hariri')}</button>
                                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(b.id)}>{t('Futa')}</button>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="empty-state">
                        <h3>{t('Hakuna matawi yaliyopo')}</h3>
                        <p>{t('Ongeza tawi jipya kwa kubonyeza "Ongeza Tawi".')}</p>
                    </div>
                )}
            </div>

            {showForm && (
                <div className="modal-overlay" onClick={closeForm}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editing ? t('Hariri Tawi') : t('Ongeza Tawi')}</h3>
                            <button className="modal-close" onClick={closeForm}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>{t('Jina la Tawi *')}</label>
                                        <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                                    </div>
                                    <div className="form-group">
                                        <label>{t('Msimbo')}</label>
                                        <input value={form.code} onChange={e => setForm({...form, code: e.target.value})} />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>{t('Mkoa / Eneo')}</label>
                                        <input value={form.region} onChange={e => setForm({...form, region: e.target.value})} />
                                    </div>
                                    <div className="form-group">
                                        <label>{t('Wilaya')}</label>
                                        <input value={form.district} onChange={e => setForm({...form, district: e.target.value})} />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>{t('Mzazi (Tawi la Juu)')}</label>
                                        <select value={form.parent_id || ''} onChange={e => setForm({...form, parent_id: e.target.value})}>
                                            <option value="">{t('— None —')}</option>
                                            {branches.filter(x => !editing || x.id !== editing.id).map(x => (
                                                <option key={x.id} value={x.id}>{x.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>{t('Hali')}</label>
                                        <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                                            <option value="active">{t('Active')}</option>
                                            <option value="inactive">{t('Inactive')}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeForm}>{t('Ghairi')}</button>
                                <button type="submit" className="btn btn-primary">{editing ? t('Hifadhi Mabadiliko') : t('Ongeza Tawi')}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Import Modal */}
            {showImport && (
                <div className="modal-overlay" onClick={() => setShowImport(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{t('Import Matawi (CSV)')}</h3>
                            <button className="modal-close" onClick={() => setShowImport(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <p>{t('You can download a sample CSV')} <a href="/branches-sample.csv" target="_blank" rel="noreferrer">{t('here')}</a>.</p>
                            <div className="form-group">
                                <input type="file" accept=".csv,text/csv" onChange={e => setImportFile(e.target.files[0] || null)} />
                            </div>

                            {importResult && (
                                <div className="import-result">
                                    <p>{t('Created:')} {importResult.summary.created} — {t('Skipped:')} {importResult.summary.skipped} — {t('Errors:')} {importResult.summary.errors}</p>
                                    {importResult.errors && importResult.errors.length > 0 && (
                                        <div className="import-errors">
                                            <h4>{t('Errors')}</h4>
                                            <ul>
                                                {importResult.errors.map((er, idx) => (
                                                    <li key={idx}>{t('Row')} {er.row}: {er.error && typeof er.error === 'string' ? er.error : JSON.stringify(er.error)}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setShowImport(false)}>{t('Cancel')}</button>
                            <button className="btn btn-primary" onClick={async () => {
                                if (!importFile) { alert(t('Please select a CSV file')); return; }
                                try {
                                    const res = await api.importBranches(importFile);
                                    setImportResult(res);
                                    await loadBranches();
                                } catch (err) {
                                    console.error(err);
                                    alert(err.message || t('Import failed'));
                                }
                            }}>{t('Upload')}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Branches;
